import React, { useMemo } from "react";
import { ActionGroup } from "shared-ts-ui-action";
import DarkModeRoundedIcon from "@mui/icons-material/DarkModeRounded";
import LightModeRoundedIcon from "@mui/icons-material/LightModeRounded";
import SettingsBrightnessOutlinedIcon from "@mui/icons-material/SettingsBrightnessOutlined";
import { useThemeUserSetting } from "./useThemeUserSetting";
import { useTranslation } from "react-i18next";
import { ThemeName } from "./ThemeName";
import { TFunction } from "i18next";

const getThemeDisplayInfo = (
  theme: ThemeName,
  t: TFunction<"shared-ts-ui-app-theme-provider">
) => {
  const themeConfig = {
    dark: {
      name: t("Dark"),
      icon: <DarkModeRoundedIcon />,
    },
    light: {
      name: t("Light"),
      icon: <LightModeRoundedIcon />,
    },
    auto: {
      name: t("Auto"),
      icon: <SettingsBrightnessOutlinedIcon />,
    },
  };

  return themeConfig[theme];
};

export const useChangeThemeAction = (): ActionGroup => {
  const { t } = useTranslation("shared-ts-ui-app-theme-provider");
  const [theme, setAppTheme] = useThemeUserSetting();

  return useMemo(() => {
    const currentThemeInfo = getThemeDisplayInfo(theme as ThemeName, t);
    const darkThemeInfo = getThemeDisplayInfo("dark", t);
    const lightThemeInfo = getThemeDisplayInfo("light", t);
    const autoThemeInfo = getThemeDisplayInfo("auto", t);
    return {
      type: "ActionGroup",
      name: currentThemeInfo.name,
      icon: currentThemeInfo.icon,
      selectable: false,
      selected: false,
      preferences: {
        displayInline: false,
        hideSelected: true,
        hideGroupNodeSelected: true,
      },
      items: [
        {
          type: "Action",
          name: darkThemeInfo.name,
          icon: darkThemeInfo.icon,
          selectable: true,
          selected: theme === "dark",
          onExecute: (item) => {
            setAppTheme("dark");
          },
        },
        {
          type: "Action",
          name: lightThemeInfo.name,
          icon: lightThemeInfo.icon,
          selectable: true,
          selected: theme === "light",
          onExecute: (item) => {
            setAppTheme("light");
          },
        },
        {
          type: "Action",
          name: autoThemeInfo.name,
          icon: autoThemeInfo.icon,
          selectable: true,
          selected: theme === "auto",
          onExecute: (item) => {
            setAppTheme("auto");
          },
        },
      ],
    } as ActionGroup;
  }, [theme, setAppTheme, t]);
};
